// Shared types for the client-side analytics tracker + consent adapter.
// See ANALYTICS-SYSTEM-DESIGN.md §3–4 for the design this implements.

/** Canonical event types the tracker may emit. postMessage pass-through
 *  (heatmap.ts listenForViewerMessages) whitelists against this list. */
export const CANONICAL_EVENT_TYPES = [
  "session.start",
  "session.heartbeat",
  "session.end",
  "category.opened",
  "product.viewed",
  "product.selected",
  "product.deselected",
  "variant.changed",
  "gallery.navigated",
  "material.changed",
  "scene.product_placed",
  "scene.product_moved",
  "scene.product_removed",
  "attachment.snapped",
  "rule.blocked",
  "camera.mode_changed",
  "camera.orbit_sample",
  "configuration.saved",
  "configuration.loaded",
  "pdf.requested",
  "email.requested",
  "ar.exported",
  "viewer.loaded",
  "viewer.failed",
  "error.occurred",
  "ui.interaction",
] as const;

export type AnalyticsEventType = (typeof CANONICAL_EVENT_TYPES)[number];

export type DeviceClass = "desktop" | "tablet" | "mobile";
export type ViewportBucket = "sm" | "md" | "lg" | "xl";

/** Wire shape of one event — matches the frozen server contract exactly. */
export interface AnalyticsEvent {
  type: string;
  /** Unix ms. */
  ts: number;
  /** In-memory session GUID (page lifetime). */
  sessionId: string;
  /** Persistent first-party id — only non-null at consent tier 2. */
  visitorId: string | null;
  surface: string;
  toolId: string | null;
  market: string | null;
  language: string | null;
  device: DeviceClass;
  viewport: ViewportBucket;
  data: Record<string, unknown>;
}

/** Config-driven CMP detector — lets a deployment integrate a bespoke cookie
 *  banner without a code release (design §3.2 "Custom adapters"). */
export interface CustomAdapterConfig {
  name: string;
  /** JS expression evaluated in the host page, truthy = CMP present.
   *  e.g. "!!window.MyCmp" */
  detectExpr: string;
  /** JS expression, truthy = statistics consent granted.
   *  e.g. "window.MyCmp.allows('stats')" */
  readExpr: string;
  /** window event name that signals a consent change. */
  updateEvent?: string;
  /** Which consent category readExpr reports (informational; only
   *  statistics-like adapters affect the tracker). */
  category?: string;
}

export type ConsentFallback = "banner" | "cookieless" | "essential";

export interface ConsentConfig {
  /** "auto" = detect CMP then fall back per `fallback`. */
  mode?: string;
  fallback?: ConsentFallback;
  bannerStyle?: "minimal" | "categories";
  respectGpc?: boolean;
  /** IAB TCF v2.2 purposes that must ALL be consented for tier 2. */
  tcfPurposes?: number[];
  customAdapters?: CustomAdapterConfig[];
  privacyUrl?: string;
  /** Optional server-provided string overrides for the fallback banner. */
  strings?: Record<string, string>;
  /** Consent copy/version tag recorded with banner choices. */
  version?: string;
}

/** Response of GET {apiBase}/api/analytics/bootstrap (frozen contract). */
export interface BootstrapResponse {
  enabled: boolean;
  token: string;
  flushIntervalMs?: number;
  consent?: ConsentConfig;
  heatmaps?: unknown;
}

export type ConsentStatus = "cookieless" | "granted" | "denied";

export interface ConsentState {
  status: ConsentStatus;
  /** 0 = essential only (JS events dropped), 1 = cookieless, 2 = full. */
  tier: 0 | 1 | 2;
  /** Where the state came from: "override", "cmp:cookiebot", "own-banner",
   *  "gpc", "fallback:cookieless", ... */
  source: string;
}

export interface TrackerOptions {
  /** API origin, e.g. "https://host.example.com". Empty = same origin. */
  apiBase: string;
  surface: string;
  toolId?: string;
  market?: string;
  language?: string;
  /** Explicit consent override — value of data-3dconfig-consent or an init
   *  option: "granted" | "denied" | "cookieless" | "banner" | "auto" | "cmp:<name>". */
  consentOverride?: string | null;
  /** Element the fallback banner mounts next to (script-embed case).
   *  Defaults to document.body. */
  bannerContainer?: HTMLElement | null;
}

export type TrackFn = (type: string, data?: Record<string, unknown>) => void;
